import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../utils/firebase";
import { fetchGlobalKeyAccountsAccess } from "../api/key_accounts_endpoints";

// Per-user capability check for the Global Key Accounts dashboard.
// The policy (dashmote staff + Firestore-allowlisted emails, in workspaces
// whose entitlements enable Global) lives server-side in the dsf-table global
// modes. This hook probes it once per (user, workspace) and caches for the
// session, so the sidebar, the route guard, and the command palette all gate
// on the SAME server answer.
//
// Fail-closed: loading, error, and workspaces without Global all resolve to
// `allowed: false` — mirroring the workflow-visibility posture. The probe is
// UI-gating only; the backend re-checks on every data call, so a spoofed
// client-side answer only reaches 403ing modes.
//
// Reliability: three intermittency sources are handled —
//  1. AUTH RACE: `auth.currentUser` is null until Firebase restores the
//     session on a fresh load; reading it once left the probe unfired until a
//     lucky re-render. The uid is subscribed via onAuthStateChanged.
//  2. TRANSIENT FAILURES: a failed probe (cold start, network blip) used to
//     hide the entry for the whole session. Errors are retried with backoff
//     and never cached, so the next mount probes again.
//  3. RELOAD POP-IN: resolved answers persist in sessionStorage per
//     (uid, workspace) and serve immediately on the next load while a fresh
//     probe revalidates in the background.
const cache = new Map();

const RETRY_DELAYS_MS = [1500, 4000];

const storageKey = (key) => `gka_allowed:${key}`;

const readStored = (key) => {
  try {
    const raw = window.sessionStorage?.getItem(storageKey(key));
    return raw === "true" ? true : raw === "false" ? false : undefined;
  } catch {
    return undefined;
  }
};

const writeStored = (key, allowed) => {
  try {
    window.sessionStorage?.setItem(storageKey(key), String(allowed === true));
  } catch {
    // Storage unavailable (private mode, quota) — the Map cache still covers the session.
  }
};

export default function useGlobalKeyAccountsAccess(idClient) {
  // Firebase auth singleton, not a redux selector: the hook is consumed by
  // provider-agnostic hooks (search index, command palette) whose tests render
  // without a redux Provider, and the probe's own fetch uses the same auth
  // token anyway. No uid (signed out / test env) fails closed.
  const [uid, setUid] = useState(() => auth?.currentUser?.uid ?? null);
  useEffect(() => {
    if (!auth) return undefined;
    return onAuthStateChanged(auth, (user) => setUid(user?.uid ?? null));
  }, []);

  // Keyed on (uid, id_client): the capability is per USER, so a same-tab user
  // switch (logout/login without a full reload) can never reuse the previous
  // user's answer.
  const key = uid && idClient ? `${uid}|${idClient}` : null;
  const cached = key ? (cache.get(key) ?? readStored(key)) : undefined;
  const [state, setState] = useState({
    allowed: cached === true,
    status: cached === undefined ? "loading" : "resolved",
  });

  useEffect(() => {
    if (!key) {
      setState({ allowed: false, status: "resolved" });
      return undefined;
    }
    const hit = cache.get(key);
    if (hit !== undefined) {
      setState({ allowed: hit === true, status: "resolved" });
      return undefined;
    }
    // A sessionStorage answer serves immediately (no pop-in after a reload)
    // but still revalidates below — the allowlist can change between loads.
    const stored = readStored(key);
    let cancelled = false;
    setState(
      stored === undefined
        ? { allowed: false, status: "loading" }
        : { allowed: stored === true, status: "resolved" }
    );
    let timer = null;
    const probe = (attempt) => {
      fetchGlobalKeyAccountsAccess({ id_client: idClient })
        .then((allowed) => {
          cache.set(key, allowed === true);
          writeStored(key, allowed === true);
          if (!cancelled) setState({ allowed: allowed === true, status: "resolved" });
        })
        .catch((err) => {
          console.error(`[useGlobalKeyAccountsAccess] probe failed (attempt ${attempt + 1}):`, err);
          if (cancelled) return;
          if (attempt < RETRY_DELAYS_MS.length) {
            timer = setTimeout(() => probe(attempt + 1), RETRY_DELAYS_MS[attempt]);
            return;
          }
          // All retries exhausted: fail closed for THIS mount but never cache
          // the failure — the next mount (or reload) probes fresh.
          setState((cur) =>
            cur.status === "resolved" ? cur : { allowed: false, status: "resolved" }
          );
        });
    };
    probe(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [key, idClient]);

  return state;
}

// Test hook — resets the session cache between unit tests.
export function __resetGlobalKeyAccountsAccessCache() {
  cache.clear();
  try {
    const store = window.sessionStorage;
    for (let i = store.length - 1; i >= 0; i--) {
      const k = store.key(i);
      if (k && k.startsWith("gka_allowed:")) store.removeItem(k);
    }
  } catch {
    // No sessionStorage in this environment — nothing to clear.
  }
}
